const { omit, sum, isEmpty, groupBy, sumBy, values: objectValues, } = require('lodash');
const numeral = require('numeral');

const { idWithTenant } = require('../util');
const { boxSizes, } = require('../config');
const { cmSizes, } = require('./amazonProduct');

const { keys, entries } = Object;

const conclusiveSku = _ => _.sku || idWithTenant(_.ref);

const conditions = {
  new: { label: '新品' },
};

const deliveryRoutes = {
  amazon: { label: 'Amazonから配送', fulfillmentType: 'AFN', },
  seller: { label: '出品者から配送', fulfillmentType: 'MFN', },
};

const contentTypes = {
  normal: { label: '通常商品', },
  amazon: { label: 'Amazon商品', },
};

const priceTaxTypes = {
  included: { label: '税込' },
  excluded: { label: '税抜' },
};

const presetTags = {
  inboundShippmentError: { label: '納品エラー', color: 'danger', },
};

const labelString = product => (product.isAmazonProduct && product.fnsku) ? product.fnsku : idWithTenant(product.ref);

const computeInventory = (product, uncompletedShippingRequests) => {
  const { id, goodInventoryIds = [], ngInventoryIds = [], } = product;
  const counts = uncompletedShippingRequests.map((shippingRequest) => {
    const { items, } = shippingRequest;
    const item = items.find(_ => _.productId === product.id);
    const pickedCount = shippingRequest[['inventoryIds', product.id].join('-')]?.length || 0;
    return {
      goodQuantity: item?.goodQuantity ? item.goodQuantity - pickedCount : 0,
      ngQuantity: item?.ngQuantity ? item.ngQuantity - pickedCount : 0,
    };
  });
  const waitingToShippingGoodCount = sumBy(counts, 'goodQuantity');
  const waitingToShippingNgCount = sumBy(counts, 'ngQuantity');
  const goodInventoryWithoutAllocation = goodInventoryIds.length - waitingToShippingGoodCount;
  const ngInventoryWithoutAllocation = ngInventoryIds.length - waitingToShippingNgCount;
  return {
    goodInventory: goodInventoryIds.length,
    ngInventory: ngInventoryIds.length,
    waitingToShippingGoodCount,
    waitingToShippingNgCount,
    goodInventoryWithoutAllocation,
    ngInventoryWithoutAllocation,
  };
};

// ネコポス判定は isNeko と shippingSize === 'neko' のORで決まるため、
// トグル時は両方を揃えて更新する。解除時は shippingSize が 'neko' のときだけ落とす
// （size60等の実サイズはネコポス判定に影響しないので保持する）
const nekoToggleValues = (product) => {
  const nextNeko = !product.isNeko;
  return {
    isNeko: nextNeko,
    ...(nextNeko ? { shippingSize: 'neko' } : (product.shippingSize === 'neko' && { shippingSize: null })),
  };
};

// 商品単体でモールに出せる数（在庫バッファ控除後）
// 5モール（rakuten/shopify/mercari/yahoo/tts）に重複していた計算式を共通化したもの
const computeAvailableStock = (product, tenant, uncompletedShippingRequests) => {
  const { goodInventoryWithoutAllocation } = computeInventory(product, uncompletedShippingRequests);
  const otherLocationStock = sum(objectValues(product.otherLocationInventories || {})) || 0;
  const buffer = product.inventoryBuffer ?? tenant?.inventoryBuffer ?? 0;
  return Math.max(0, goodInventoryWithoutAllocation + otherLocationStock - buffer);
};

// セット品の作成可能数 = 構成品ごとの (バッファ控除後の在庫 / 必要数) の最小値
// 例: a在庫20(必要2) / b在庫28(必要3) → min(floor(20/2), floor(28/3)) = min(10, 9) = 9
const computeSetInventory = (setProduct, tenant, productsById, uncompletedShippingRequests) => {
  const counts = (setProduct.items || []).map(({ productId, quantity }) => {
    const product = productsById[productId];
    if(product == null || !(quantity > 0)) return 0;
    return Math.floor(computeAvailableStock(product, tenant, uncompletedShippingRequests) / quantity);
  });
  return isEmpty(counts) ? 0 : Math.min(...counts);
};

// モールへ送る在庫数。セット品かどうかで算出方法が変わる
const computeMallStockQuantity = (product, tenant, productsById, uncompletedShippingRequests) => {
  if(!product.isSet) return computeAvailableStock(product, tenant, uncompletedShippingRequests);

  // セット品自身のバッファはさらに引く。ただし tenant 既定値は継承しない
  // （構成品側で既にテナント既定バッファが引かれており、継承すると二重に引かれるため）
  const setBuffer = product.inventoryBuffer ?? 0;
  return Math.max(0, computeSetInventory(product, tenant, productsById, uncompletedShippingRequests) - setBuffer);
};

// 出荷依頼itemsのうちセット品を構成品に展開し、同一productIdをマージする
// セットと単品の同時購入や、複数variantが同一productに紐づくケースをまとめる
// NOTE: 呼び出し元によって item が持つ数量フィールドが異なる（CSV取込は goodQuantity のみ）ため、
//       元の item に無かったフィールドは足さない
const expandSetItems = (items, productsById) => {
  const scaleQuantity = (item, key, multiplier) => (
    item[key] == null ? {} : { [key]: (item[key] || 0) * multiplier }
  );
  const expanded = (items || []).flatMap((item) => {
    const product = productsById[item.productId];
    if(!product?.isSet) return [item];
    const setQuantity = item.quantity ?? item.goodQuantity ?? 0;
    return (product.items || []).map(_ => ({
      ...omit(item, ['sku', 'productId']),
      productId: _.productId,
      sku: productsById[_.productId]?.sku,
      ...scaleQuantity(item, 'goodQuantity', _.quantity),
      ...scaleQuantity(item, 'quantity', _.quantity),
      ...(item.ngQuantity == null ? {} : { ngQuantity: 0 }),
      setSource: { productId: product.id, sku: product.sku, quantity: setQuantity, },
    }));
  });
  const mergeQuantity = (rows, key) => (
    rows.every(_ => _[key] == null) ? {} : { [key]: sumBy(rows, _ => _[key] || 0) }
  );
  return objectValues(groupBy(expanded.filter(_ => _.productId != null), 'productId')).map(rows => ({
    ...rows[0],
    ...mergeQuantity(rows, 'goodQuantity'),
    ...mergeQuantity(rows, 'ngQuantity'),
    ...mergeQuantity(rows, 'quantity'),
  }));
};

const computeStorageSize = (product) => {
  if(product == null) return 'unknown';

  const { isCase, isAmazonProduct, shippingSize, storageSize, amazonProduct, } = product;
  if(!isEmpty(storageSize)) return storageSize;
  if(!isEmpty(shippingSize)) return shippingSize;
  if(!isAmazonProduct || isCase) return 'unknown';
  if(isEmpty(cmSizes(amazonProduct))) return 'unknown';

  const cmSizesSum = sum(cmSizes(amazonProduct));
  return cmSizesSum > 260 ? (
    'size260'
  ) : (
    keys(boxSizes).filter(_ => _.match(/\d/)).find(_ => cmSizesSum <= numeral(_).value()) || 'unknown'
  );
};

const fields = ({ tenant, otherProducts = [], } = {}) => {
  return {
    name: {
      label: '商品名',
      type: 'string',
      validations: {
        required: _ => !isEmpty(_),
      },
    },
    sku: {
      label: 'SKU',
      type: 'string',
      validations: {
        required: _ => !isEmpty(_),
        unique: (v, s) => !otherProducts.map(_ => _.sku).includes(v),
      },
      format: _ => _.replace(/\s/g, ''),
      hint: 'モールやカートシステムに登録されているバリエーションごとの商品管理番号です。',
    },
    barcodeInfo: {
      label: 'JANまたはバーコード情報',
      type: 'string',
    },
    itemName: {
      label: '品名',
      type: 'string',
      validations: {
        maxLength: _ => (_ || '').length <= 16,
      },
      hint: '16文字以内。指定しない場合は商品名の先頭10文字が品名になります。',
    },
    price: {
      label: '販売価格',
      type: 'integer',
      validations: {
        greaterThanOrEqualTo0: _ => _ == null || _ >= 0,
      },
    },
    priceTaxType: {
      label: '税区分',
      type: 'radio',
      options: entries(priceTaxTypes).map(([k, _]) => ({ label: _.label, value: k })),
      initialValue: 'included',
      hidden: _ => _.price == null,
    },
    requestsNeko: {
      label: 'ポスト便希望',
      type: 'boolean',
      initialValue: false,
    },
    productTagIds: {
      label: 'タグ',
      type: 'multiSelect',
      options: entries(presetTags).map(([k, _]) => ({ label: _.label, value: k, })),
      initialValue: [],
    },
    inventoryBuffer: {
      type: 'integer',
      label: '在庫バッファ数',
      hidden: _ => ['mercari', 'shopify', 'rakuten'].every(_ => !tenant.tenantOptionValues?.includes(_)),
      hint: '空の場合は一般設定の在庫バッファ数が使われます',
    },
  };
};

module.exports = {
  fields,
  caseFields: (...args) => {
    const [{ tenant, extraSellers }] = args;
    return {
      ...omit(fields(...args), ['requestsNeko', 'inventoryBuffer', 'price', 'priceTaxType']),
      contentType: {
        label: '内容種別',
        type: 'select',
        options: entries(contentTypes).map(([k, _]) => ({ label: _.label, value: k })),
      },
      ...(
        tenant?.tenantOptionValues?.includes('multiSellers') && {
          extraSellerId: {
            label: 'ショップ',
            type: 'select',
            options: [{ ...tenant, id: null, name: 'メイン' }, ...extraSellers].map(_ => ({ label: _.name, value: _.id })),
            hidden: _ => _.contentType !== 'amazon',
            initialValue: null,
          },
        }
      ),
    };
  },
  adminFields: () => {
    return {
      sku: {
        label: 'SKU',
        type: 'string',
        validations: {
          required: _ => !isEmpty(_),
        },
        hint: '慎重に変更する必要があります',
      },
      shippingSize: {
        label: 'サイズ',
        type: 'select',
        options: entries(boxSizes).map(([k, _]) => ({ label: _.label, value: k })),
      },
      storageSize: {
        label: '保管サイズ',
        type: 'select',
        options: entries(boxSizes).map(([k, _]) => ({ label: _.label, value: k })),
      },
      hasExpiration: {
        label: '期限あり',
        type: 'boolean',
      },
    };
  },
  amazonProductFields: ({ otherProducts = [], } = {}) => {
    return {
      query: {
        label: '検索キーワード',
        type: 'string',
        placeholder: '商品名やASIN、JAN等で検索できます',
      },
      sku: {
        label: 'SKU',
        type: 'string',
        validations: {
          required: _ => !isEmpty(_),
          unique: (v, s) => !otherProducts.map(_ => _.sku).includes(v),
        },
        format: _ => _.replace(/\s/g, ''),
        hint: 'モールやカートシステムに登録されているバリエーションごとの商品管理番号です。',
      },
      itemName: {
        label: '品名',
        type: 'string',
        validations: {
          maxLength: _ => (_ || '').length <= 16,
        },
        hint: '16文字以内。指定しない場合は商品名の先頭10文字が品名になります。',
      },
      price: {
        label: '販売価格',
        type: 'integer',
        validations: {
          required: _ => _ != null,
          greaterThan0: _ => _ == null || _ > 0,
        },
      },
      deliveryRoute: {
        label: '配送方法',
        type: 'select',
        options: entries(deliveryRoutes).map(([k, v]) => ({ label: v.label, value: k })),
        validations: {
          required: _ => !isEmpty(_),
        },
        initialValue: 'amazon',
      },
      condition: {
        label: 'コンディション',
        type: 'select',
        options: entries(conditions).map(([k, v]) => ({ label: v.label, value: k })),
        validations: {
          required: _ => !isEmpty(_),
        },
        initialValue: 'new',
      },
      conditionNote: {
        label: 'コンディション説明',
        type: 'text',
      },
    };
  },
  caseItemFields: ({ items = [], products = [], productsById = {}, } = {}) => {
    return {
      productId: {
        label: '商品',
        type: 'select',
        options: products.map(_ => ({ label: `${idWithTenant(_.ref)} [${_.sku}] ${_.name}`, value: _.id })),
        validations: {
          required: _ => !isEmpty(_),
          unique: v => items.filter(_ => _.productId === v).length < 2,
        },
        hint: '商品ID、SKU、ASINで検索できます',
      },
      quantity: {
        label: '数量',
        type: 'integer',
        validations: {
          greaterThan0: _ => _ > 0,
        },
      },
    };
  },
  setFields: (...args) => {
    const [{ tenant }] = args;
    return {
      ...omit(fields(...args), ['requestsNeko', 'price', 'priceTaxType']),
      inventoryBuffer: {
        type: 'integer',
        label: '在庫バッファ数',
        hint: '空の場合はバッファなし。構成品側のバッファは別途引かれます',
      },
    };
  },
  setItemFields: ({ items = [], products = [], } = {}) => {
    return {
      productId: {
        label: '構成品',
        type: 'select',
        options: products.map(_ => ({ label: `${idWithTenant(_.ref)} [${_.sku}] ${_.name}`, value: _.id })),
        validations: {
          required: _ => !isEmpty(_),
          unique: v => items.filter(_ => _.productId === v).length < 2,
        },
        hint: '商品ID、SKU、ASINで検索できます',
      },
      quantity: {
        label: '数量',
        type: 'integer',
        validations: {
          greaterThan0: _ => _ > 0,
        },
      },
    };
  },
  computeAvailableStock,
  computeSetInventory,
  computeMallStockQuantity,
  expandSetItems,
  conclusiveSku,
  conditions,
  deliveryRoutes,
  labelString,
  computeInventory,
  presetTags,
  priceTaxTypes,
  computeStorageSize,
  contentTypes,
  nekoToggleValues,
};
